import React, { useState } from "react";
import { Button, CircularProgress, Card, CardContent, Typography, TextField, Box, useTheme, alpha } from "@mui/material";
import { createAxiosInstance } from "../../../api";
import SweetAlert from "sweetalert2";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import config from "config";
import errorIcon from "../../../assets/error-icon.svg";

const MessageBox = (props) => {
  const { tutor } = props;
  const { tutorId } = useParams();
  const { _id: tutorProfileId } = useSelector(
    (state) => state.profile.information
  );
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const { userId } = useSelector((state) => state.main);
  const [sendStatus, setSendStatus] = useState(false);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();

  const sendMessage = (e) => {
    e.stopPropagation();
    if (message.trim().length === 0 && userId) {
      SweetAlert.fire({
        icon: errorIcon,
        imageHeight: "100px",
        title: "Oops...",
        text: "Your message is empty, please input your message.",
        confirmButtonColor: "#712d94",
      });
      return;
    }
    if (!userId) {
      SweetAlert.fire({
        // position: 'top-end',
        icon: "info",
        iconColor: "#712d93",
        imageHeight: "100px",
        title: "Confirm",
        html: `You need to <a className="${config["primary-text-colour-class"]}" href='/signin'>sign in</a> or <a href='/signup?guest'>sign up</a> to send a message.`,
        width: 600,
        confirmButtonColor: config["primary-colour-mui"],
        cancelButtonColor: "#000000",
        showCancelButton: true,
        confirmButtonText: "Sign Up",
        customClass: {
          icon: "no-before-icon",
        },
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/signup?guest");
        }
      });
      return;
    }
    setLoading(true);
    createAxiosInstance()
      .post(`/api/messages`, {
        senderId: userId,
        receiverId: tutor.user._id,
        content: message,
      })
      .then((response) => {
        setMessage("");
        setSendStatus(true);
        setTimeout(() => setSendStatus(false), 1000);
      })
      .catch((err) => {
        SweetAlert.fire({
          imageUrl: errorIcon,
          imageHeight: "100px",
          title: "Oops...",
          text:
            err?.response?.message || "Message send failed, please try again.",
          confirmButtonColor: "#712d94",
        });
      })
      .finally(() => setLoading(false));
  };
  return (
    <Card sx={{ 
      borderRadius: 3,
      boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.08)}`,
      border: `1px solid ${theme.palette.divider}`,
      overflow: 'hidden',
      opacity: tutorId === tutorProfileId ? 0.6 : 1,
      pointerEvents: tutorId === tutorProfileId ? "none" : "auto"
    }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        <Typography variant="h5" sx={{ 
          fontWeight: 700, 
          color: theme.palette.text.primary, 
          mb: 3,
          fontSize: { xs: '1.25rem', sm: '1.35rem', md: '1.5rem' },
          wordBreak: 'break-word',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -8,
            left: 0,
            width: 40,
            height: 3,
            backgroundColor: theme.palette.primary.main,
            borderRadius: 2
          }
        }}>
          Message {tutor.user.firstName}
        </Typography>
        
        {!sendStatus && (
          <TextField
            multiline
            rows={5}
            fullWidth
            placeholder="Type your message here"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={loading || tutorId === tutorProfileId}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                fontSize: '1rem',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                }
              }
            }}
          />
        )}

        {sendStatus && (
          <Box
            sx={{
              height: 148,
              width: "100%",
              backgroundColor: alpha('#00AB55', 0.1),
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontSize: "1.375rem",
              color: "#00AB55",
              fontWeight: 600,
              borderRadius: 2,
              border: `1px solid ${alpha('#00AB55', 0.2)}`,
              mb: 2
            }}
          >
            Message sent!
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            disabled={loading || tutorId === tutorProfileId}
            variant="contained"
            onClick={sendMessage}
            sx={{
              backgroundColor: theme.palette.primary.main,
              borderRadius: 2,
              px: 3,
              py: 1,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
              '&:disabled': {
                backgroundColor: 'rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.26)'
              }
            }}
          >
            {loading && (
              <CircularProgress
                size={20}
                sx={{ color: "white", mr: 1 }}
              />
            )}
            Send
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MessageBox;
